import React, {useState, useCallback, useEffect} from 'react';
import Stack from '@mui/material/Stack';
import {MyTypography} from '../../common/components';
import MyButton from '../../common/components/MyButton';
import OcrAnnotation from '../OcrAnnotation';
import Fuse from 'fuse.js';
import {useClinicDropdownDialog} from '../../clinics/components/ClinicDropdownHookFormSelect';

export function useOcrClinicForm({setValue, referringClinicInputName}) {
    const [options, setOptions] = useState([]);
    const { openDialog: openAddClinicDialog } = useClinicDropdownDialog();

    const [ocrDetails, setOcrDetails] = useState({
        referringClinicInputName,
        ocrSuggestionExistOnDropdown: false,
        referringClinicMatch: null,
        note: null,
    });

    const onGetMatchingItem = useCallback((referringClinicName) => {
        if (!referringClinicName || !options || options.length === 0) {
            return {id: null, name: referringClinicName};
        }

        // Fuse.js options (you can tweak threshold for fuzziness)
        const fuse = new Fuse(options, {
            keys: ['name'],
            includeScore: true,
            threshold: 0.3, // lower = stricter match
        });

        const result = fuse.search(referringClinicName);

        if (result.length > 0) {
            // Take the best match
            const bestMatch = result[0];
            // we return the clinic id
            return {
                id: bestMatch.item.id,
                name: bestMatch.item.name,
            };
        }

        // otherwise we just return the clinic name
        return {id: null, name: referringClinicName};
    }, [options]);

    const triggerSelect = useCallback((value) => () => {

        const match = onGetMatchingItem(value);

        // toggle add clinic dialog
        if (match.id) {
            setValue('referring_clinic', match.id);
        } else {
            openAddClinicDialog(match.name);
        }
    }, [setValue, onGetMatchingItem, openAddClinicDialog]);

    const onOptionsLoaded = useCallback((data) => {
        setOptions(data);
    }, []);

    useEffect(() => {
        const match = onGetMatchingItem(referringClinicInputName);

        if (match.id) {
            setOcrDetails({
                referringClinicInputName,
                ocrSuggestionExistOnDropdown: true,
                referringClinicMatch: match.id,
                note: `We found '${match.name}' as a possible match for '${referringClinicInputName}'`,
            });
            // setTimeout(() => {
            //     triggerSelectClinic(match.id);
            // }, 400);
        } else {
            setOcrDetails({
                referringClinicInputName,
                ocrSuggestionExistOnDropdown: false,
                referringClinicMatch: match.name,
                note: `We can't find '${referringClinicInputName}' on the Clinic list. You can create this clinic by clicking the first option on the Clinic dropdown.`,
            });
        }

        return () => {
            setOcrDetails({
                referringClinicInputName: null,
                ocrSuggestionExistOnDropdown: false,
                referringClinicMatch: null,
                note: null,
            });
        };
    }, [referringClinicInputName, onGetMatchingItem, triggerSelect]);

    return {
        onOptionsLoaded,
        triggerSelect,
        ocrDetails,
    };
}

export default function ReferringClinicOcrAnnotation({triggerSelect, ocrDetails, title="AI Detected Clinic"}) {
    if (!ocrDetails.referringClinicMatch) {
        return null;
    }

    return (
        <OcrAnnotation title={title}>
            <Stack direction="column"
                   spacing={1}>
                <MyTypography variant="caption" sx={{fontWeight: 600, color: 'inherit'}}>{ocrDetails.referringClinicInputName}</MyTypography>

                <MyButton variant="contained"
                          size="small"
                          color="info"
                          onClick={triggerSelect}
                          fullWidth
                          disableElevation>USE THIS CLINIC</MyButton>
                {
                    ocrDetails.note &&
                    <MyTypography variant="caption" sx={{color: 'inherit'}}>Note: {ocrDetails.note}</MyTypography>
                }
            </Stack>
        </OcrAnnotation>
    );
}
